// ─── Tipos e dados mockados — Bolsão (Backoffice) ─────────────────────────────

export type BolsaoStatus = 'Dentro do esperado' | 'Abaixo do esperado' | 'Teto atingido';

export interface Bolsao {
  id: string;
  payfac: string;
  /** ID da company (payfac) */
  payfacId: string;
  /** Percentual sobre TPV configurado (ex.: 3, 2.5) */
  percentTpv: number;
  /** Teto em R$ — null = sem teto */
  teto: number | null;
  /** Bolsão esperado em R$ — null enquanto "Calculando..." */
  esperado: number | null;
  /** Bolsão adquirido em R$ — null enquanto "Calculando..." */
  adquirido: number | null;
  configuradoEm: string;
  ultimaAlteracao: { usuario: string; data: string };
}

export interface CompanyOption {
  id: string;
  nome: string;
  cnpj: string;
}

// ─── Derivações ───────────────────────────────────────────────────────────────

/** Gap descoberto em R$ — 0 quando não há gap ou ainda está calculando. */
export function gapDe(b: Bolsao): number {
  if (b.esperado === null || b.adquirido === null) return 0;
  return Math.max(0, b.esperado - b.adquirido);
}

/** Status derivado — sem tolerância: qualquer gap > 0 já é "Abaixo do esperado".
 *  Bolsão recém-criado (sem TPV acumulado ainda) conta como "Dentro do esperado". */
export function statusDe(b: Bolsao): BolsaoStatus {
  if (b.esperado === null || b.adquirido === null) return 'Dentro do esperado';
  if (b.teto !== null && b.adquirido >= b.teto) return 'Teto atingido';
  if (b.esperado - b.adquirido > 0) return 'Abaixo do esperado';
  return 'Dentro do esperado';
}

export function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

export function formatPercent(value: number): string {
  return `${value.toLocaleString('pt-BR')}%`;
}

// ─── Dados mockados ───────────────────────────────────────────────────────────

export const INITIAL_BOLSOES: Bolsao[] = [
  {
    id: 'BOL-0007',
    payfac: 'Credpago Soluções',
    payfacId: '98',
    percentTpv: 3,
    teto: null,
    esperado: 210450.0,
    adquirido: 148900.0,
    configuradoEm: '12/06/2026',
    ultimaAlteracao: { usuario: 'Fernanda Lima', data: '12/06/2026' },
  },
  {
    id: 'BOL-0011',
    payfac: 'Zenpag Tecnologia de Pagamentos',
    payfacId: '651',
    percentTpv: 2.5,
    teto: 300000.0,
    esperado: 92750.0,
    adquirido: 61020.0,
    configuradoEm: '03/07/2026',
    ultimaAlteracao: { usuario: 'Ricardo Tavares', data: '18/07/2026' },
  },
  {
    id: 'BOL-0002',
    payfac: 'Fintech Pagamentos S.A.',
    payfacId: '37',
    percentTpv: 3,
    teto: 500000.0,
    esperado: 500000.0,
    adquirido: 512300.0,
    configuradoEm: '02/03/2026',
    ultimaAlteracao: { usuario: 'Fernanda Lima', data: '02/03/2026' },
  },
  {
    id: 'BOL-0004',
    payfac: 'Pay360 Brasil Ltda',
    payfacId: '214',
    percentTpv: 2.5,
    teto: 300000.0,
    esperado: 187500.0,
    adquirido: 191240.0,
    configuradoEm: '15/04/2026',
    ultimaAlteracao: { usuario: 'Ricardo Tavares', data: '15/04/2026' },
  },
  {
    id: 'BOL-0009',
    payfac: 'Lunar Pay Instituição de Pagamento',
    payfacId: '12',
    percentTpv: 3,
    teto: 250000.0,
    esperado: 250000.0,
    adquirido: 318400.0,
    configuradoEm: '20/05/2026',
    ultimaAlteracao: { usuario: 'Camila Duarte', data: '11/08/2026' },
  },
  {
    id: 'BOL-0013',
    payfac: 'Trampolim Pagamentos',
    payfacId: '305',
    percentTpv: 2,
    teto: 120000.0,
    esperado: 74320.0,
    adquirido: 99500.0,
    configuradoEm: '28/07/2026',
    ultimaAlteracao: { usuario: 'Camila Duarte', data: '28/07/2026' },
  },
  {
    id: 'BOL-0015',
    payfac: 'NovaPag Meios de Pagamento',
    payfacId: '77',
    percentTpv: 3,
    teto: 400000.0,
    esperado: null,
    adquirido: null,
    configuradoEm: '25/08/2026',
    ultimaAlteracao: { usuario: 'Ricardo Tavares', data: '25/08/2026' },
  },
];

// Companies disponíveis no Select do "Novo bolsão".
// Inclui as que já têm bolsão — a regra de 1 bolsão por company é validada ao salvar.
export const COMPANIES: CompanyOption[] = [
  { id: '37', nome: 'Fintech Pagamentos S.A.', cnpj: '12.480.331/0001-09' },
  { id: '214', nome: 'Pay360 Brasil Ltda', cnpj: '08.945.612/0001-53' },
  { id: '98', nome: 'Credpago Soluções', cnpj: '31.872.495/0001-04' },
  { id: '651', nome: 'Zenpag Tecnologia de Pagamentos', cnpj: '45.118.203/0001-77' },
  { id: '12', nome: 'Lunar Pay Instituição de Pagamento', cnpj: '27.554.980/0001-31' },
  { id: '305', nome: 'Trampolim Pagamentos', cnpj: '19.203.887/0001-46' },
  { id: '77', nome: 'NovaPag Meios de Pagamento', cnpj: '52.610.774/0001-88' },
  { id: '143', nome: 'Vela Pagamentos Ltda', cnpj: '36.998.120/0001-65' },
  { id: '520', nome: 'Arpex Soluções Financeiras', cnpj: '41.277.508/0001-22' },
  { id: '8', nome: 'Kudo Pay Tecnologia', cnpj: '58.443.916/0001-10' },
];
